import { usePreferences } from "hooks/use-preferences"
import { produce } from "immer"
import PropTypes from "prop-types"
import { createContext, useMemo, useReducer } from "react"
import { INSTRUMENT_METADATA_SHAPE, MODEL_METADATA_SHAPE, POSITION_SET_SHAPE } from "../helpers/shapes"

export const AiTeammateContext = createContext()

const PREFERENCE_SUBTYPE = "ai-teammate"

const PREFERENCE_FIELDS = [
  "drawerWidth",
]

const DEFAULT_STATE = {
  error: null,
  isDrawerResizing: false,
}

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setError":
        draft.error = action.error
        break
      case "setDrawerWidth":
        draft.drawerWidth = action.drawerWidth
        break
      case "setPreferences":
        Object.assign(draft, action.preferences)
        break
      case "setIsDrawerResizing":
        draft.isDrawerResizing = action.isDrawerResizing
        break
    }
  })
}

function setError(error) {
  return { type: "setError", error }
}

function setDrawerWidth(drawerWidth) {
  return { type: "setDrawerWidth", drawerWidth }
}

function setPreferences(preferences) {
  return { type: "setPreferences", preferences }
}

function setIsDrawerResizing(isDrawerResizing) {
  return { type: "setIsDrawerResizing", isDrawerResizing }
}

export function AiTeammateProvider({
  children,
  isOpen,
  onClose,
  modelId,
  instrument,
  base,
  modelMetadata,
  resourceMetadata,
  benchmarkMetadata,
  currency,
  startDate,
  endDate,
}) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setError: (...args) => dispatch(setError(...args)),
    setDrawerWidth: (...args) => dispatch(setDrawerWidth(...args)),
    setPreferences: (...args) => dispatch(setPreferences(...args)),
    setIsDrawerResizing: (...args) => dispatch(setIsDrawerResizing(...args)),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...state,
    ...contextUpdaters,
    isOpen,
    onClose,
    modelId,
    instrument,
    base,
    modelMetadata,
    resourceMetadata,
    benchmarkMetadata,
    currency,
    startDate,
    endDate,
    isPreferencesLoaded,
    hasError: !!state.error,
  }), [
    state,
    contextUpdaters,
    isOpen,
    onClose,
    modelId,
    instrument,
    base,
    modelMetadata,
    resourceMetadata,
    benchmarkMetadata,
    currency,
    startDate,
    endDate,
    isPreferencesLoaded,
  ])

  return <AiTeammateContext.Provider value={contextValue}>
    {children}
  </AiTeammateContext.Provider>
}

AiTeammateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  modelId: PropTypes.string,
  instrument: POSITION_SET_SHAPE,
  base: POSITION_SET_SHAPE,
  modelMetadata: MODEL_METADATA_SHAPE,
  resourceMetadata: INSTRUMENT_METADATA_SHAPE,
  benchmarkMetadata: INSTRUMENT_METADATA_SHAPE,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  currency: PropTypes.string,
}
